import type { VaultPlainItem } from '../types/vault.types.js';
import { SettingsService } from './settings.service.js';

export interface VaultProjectContext {
    projectId: string;
    projectSlug: string;
    tags: string[];
}

export interface VaultProjectTagOptions {
    includeProjectTags?: boolean;
}

export class VaultProjectContextService {
    private readonly settings: SettingsService;

    constructor(private readonly cwd: string = process.cwd()) {
        this.settings = new SettingsService(cwd);
    }

    detect(): VaultProjectContext | null {
        const projectId = this.settings.getProjectId();
        if (!projectId) return null;

        const repoUrl = this.settings.resolveGitRepoUrl();
        const projectSlug = repoUrl ? slugFromRepoUrl(repoUrl) : slugFromProjectId(projectId);

        return {
            projectId,
            projectSlug,
            tags: [`project:${projectId}`, `project-slug:${projectSlug}`],
        };
    }

    filterToCurrentProject<T extends Pick<VaultPlainItem, 'tags'>>(items: T[]): T[] {
        const context = this.detect();
        if (!context) return [];
        return items.filter((item) => context.tags.some((tag) => item.tags.includes(tag)));
    }

    buildItemTags(tags: string[] = [], options: VaultProjectTagOptions = {}): string[] {
        const includeProjectTags = options.includeProjectTags !== false;
        const projectTags = includeProjectTags ? this.detect()?.tags ?? [] : [];
        return [...new Set([...tags, ...projectTags])];
    }
}

function slugFromRepoUrl(repoUrl: string): string {
    return slugifyProject(repoUrl);
}

function slugFromProjectId(projectId: string): string {
    const withoutHash = projectId.replace(/^[a-f0-9]{12}-/, '');
    return slugifyProject(withoutHash || projectId);
}

function slugifyProject(value: string): string {
    return value
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();
}
